import { FC, useCallback, useState } from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Virtuoso } from 'react-virtuoso'
import {
  LayersOutlined,
  Close,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'
import { Button, IconButton } from '@common/material'
import { localizedStrings } from '@core/strings'

type MapLayersListProps = {
  projectMapLayers: ProjectMapLayer[]
  onToggleLayerVisibility: (layerId: number | string) => void
  onToggleAllLayersVisibility: (visible: boolean) => void
}

type MinimalLayer = Pick<ProjectMapLayer, 'id' | 'name' | 'visible'>

export const MapLayersList: FC<MapLayersListProps> = ({
  projectMapLayers,
  onToggleLayerVisibility,
  onToggleAllLayersVisibility,
}) => {
  const [isVisible, setIsVisible] = useState(false)

  const allLayersVisible = projectMapLayers.every((layer) => layer.visible)

  const renderItem = useCallback(
    (_index: number, layer: MinimalLayer) => (
      <Flex
        key={layer.id}
        sx={{
          alignItems: 'center',
          px: 1.5,
          py: 1,
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'grey.200',
          },
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Flex
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: 'grey.200',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <LayersOutlined sx={{ color: 'primary.main', fontSize: 18 }} />
        </Flex>
        <Text variant='body2' color='text.primary' noWrap flex={1}>
          {layer.name}
        </Text>
        <IconButton
          onClick={() => onToggleLayerVisibility(layer.id)}
          sx={{ cursor: 'pointer', p: 0.5 }}
        >
          {layer.visible ? (
            <VisibilityOutlined fontSize='small' />
          ) : (
            <VisibilityOffOutlined fontSize='small' />
          )}
        </IconButton>
      </Flex>
    ),
    [onToggleLayerVisibility],
  )

  if (isVisible) {
    return (
      <Box
        sx={{
          width: 280,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            px: 2,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Text variant='subtitle1' fontWeight={600} color='text.primary'>
            Layers
          </Text>
          <IconButton
            size='small'
            onClick={() => setIsVisible(false)}
            sx={{ ml: 'auto' }}
          >
            <Close fontSize='small' />
          </IconButton>
        </Flex>

        <Virtuoso
          style={{ height: '20dvh' }}
          data={projectMapLayers}
          itemContent={renderItem}
        />

        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            variant='text'
            color='primary'
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => onToggleAllLayersVisibility(!allLayersVisible)}
          >
            {allLayersVisible
              ? localizedStrings.hideAllLayers
              : localizedStrings.showAllLayers}
          </Button>
        </Flex>
      </Box>
    )
  }

  return (
    <IconButton
      onClick={() => setIsVisible(true)}
      sx={{
        backgroundColor: 'white',
        color: 'text.secondary',
        boxShadow: 1,
        width: 56,
        height: 56,
        borderRadius: '50%',
        '&:hover': {
          backgroundColor: 'grey.200',
        },
      }}
    >
      <LayersOutlined />
    </IconButton>
  )
}
